import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

import { ConfigurationRoute } from 'app/providers/semantria-api/configuration-route.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  IndustryPack,
  IndustryPackService,
} from 'app/providers/semantria-api/industry-pack.service';
import {
  LanguageTemplate,
  LanguageTemplateService,
} from 'app/providers/semantria-api/language-template.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { ConfigurationListEntry } from '../configuration-route-configurations/configuration-route-configurations.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-included-configurations-form',
  templateUrl: './included-configurations-form.component.html',
  styleUrls: ['./included-configurations-form.component.scss'],
  standalone: false,
})
export class IncludedConfigurationsFormComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() configurationRoute: ConfigurationRoute;
  @Input() isSaving: boolean;

  @Output() includedConfigurationsChange = new EventEmitter<ConfigurationListEntry[]>();

  isLoading;

  groupId: string;

  configurations: SemantriaConfiguration[];
  industryPacks: IndustryPack[];
  languageTemplates: LanguageTemplate[];

  availableConfigurations: ConfigurationListEntry[];
  includedConfigurations: ConfigurationListEntry[];

  filteredAvailableConfigurations: ConfigurationListEntry[];

  search$ = new Subject<string>();
  unsub$ = new Subject<void>();

  filterVal = '';

  constructor(
    private groupService: GroupService,
    private configurationService: ConfigurationService,
    private industryPackService: IndustryPackService,
    private languageTemplateService: LanguageTemplateService
  ) {
    this.search$
      .pipe(
        takeUntil(this.unsub$),
        distinctUntilChanged(),
        tap((val) => (this.filterVal = val))
      )
      .subscribe((val) => {
        this.filterConfigurations(val);
      });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.groupId = this.groupService.getSelectedGroupId(true);
    Promise.all([
      this.configurationService.getConfigurations(false, this.groupService.getSelectedGroupId()),
      this.industryPackService.getIndustryPacks(),
      this.languageTemplateService.getLanguageTemplates(),
    ])
      .then((res) => {
        this.configurations = res[0];
        this.industryPacks = res[1];
        this.languageTemplates = res[2];

        if (this.configurationRoute) {
          this.assembleAvailableConfigurationsList();
          this.assembleIncludedConfigurationsList();
          this.filterConfigurations();
        }
      })
      .finally(() => (this.isLoading = false));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.configurationRoute?.currentValue && this.configurations) {
      this.assembleAvailableConfigurationsList();
      this.assembleIncludedConfigurationsList();
      this.filterConfigurations();
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }

  private assembleAvailableConfigurationsList() {
    this.availableConfigurations = [];

    const languageIds = {};

    const collectConfigurations = (source, pillType) => {
      source.forEach((c) => {
        languageIds[c.language_id] = languageIds[c.language_id] || [];
        languageIds[c.language_id].push({
          configuration: c,
          pillType,
        });
      });
    };

    collectConfigurations(this.configurations, 'CO');
    collectConfigurations(this.industryPacks, 'IP');
    collectConfigurations(this.languageTemplates, 'LT');

    for (const id of Object.keys(languageIds)) {
      languageIds[id].sort((l1, l2) => l1.configuration.name.localeCompare(l2.configuration.name));

      this.availableConfigurations.push({
        languageId: id,
        languageName: languageIds[id][0].configuration.language_name,
        configurations: languageIds[id],
      });
    }

    this.availableConfigurations.sort((l1, l2) => l1.languageName.localeCompare(l2.languageName));
  }

  private assembleIncludedConfigurationsList() {
    this.includedConfigurations = Object.keys(this.configurationRoute.configuration_map).map(
      (langId) => {
        const languageConfigs = this.availableConfigurations.find((ln) => ln.languageId === langId);
        const configurations = languageConfigs.configurations;
        const cidx = configurations.findIndex(
          (ac) => ac.configuration.id === this.configurationRoute.configuration_map[langId]
        );
        const entry = configurations.splice(cidx, 1)[0];

        return {
          languageId: langId,
          languageName: entry.configuration.language_name,
          configurations: [{ configuration: entry.configuration, pillType: entry.pillType }],
        };
      }
    );

    this.includedConfigurations.sort((l1, l2) => l1.languageName.localeCompare(l2.languageName));
  }

  private filterConfigurations(value = '') {
    const lcValue = value.toLocaleLowerCase();

    this.filteredAvailableConfigurations = [];

    const filterFn = (target) => (c) => {
      const filteredConfigs = c.configurations.filter((e) => {
        const includedLanguage = this.includedConfigurations.find(
          (ic) => ic.languageId === c.languageId
        );

        return (
          e.configuration.name.toLocaleLowerCase().includes(lcValue) &&
          !e.configuration.id.endsWith('prm') &&
          !includedLanguage?.configurations.includes(e)
        );
      });
      if (c.languageName.toLocaleLowerCase().includes(lcValue) || filteredConfigs.length) {
        target.push({ ...c, configurations: filteredConfigs });
      }
    };

    this.availableConfigurations.forEach(filterFn(this.filteredAvailableConfigurations));
  }

  onConfigurationAdded($event) {
    this.removeConfigurationFrom(this.filteredAvailableConfigurations, $event);

    this.removeConfigurationFrom(this.availableConfigurations, $event);

    this.addConfigurationTo(
      this.includedConfigurations,
      $event,
      this.availableConfigurations,
      this.filteredAvailableConfigurations
    );

    this.triggerChangeDetection();

    this.includedConfigurationsChange.emit(this.includedConfigurations);
  }

  onConfigurationRemoved($event) {
    this.removeConfigurationFrom(this.includedConfigurations, $event, true);

    if (
      this.filteredAvailableConfigurations.some(
        (c) => c.languageId === $event.configuration.language_id
      )
    ) {
      this.addConfigurationTo(this.filteredAvailableConfigurations, $event);
    }
    this.addConfigurationTo(this.availableConfigurations, $event);

    this.triggerChangeDetection();

    this.includedConfigurationsChange.emit(this.includedConfigurations);
  }

  private removeConfigurationFrom(
    source: ConfigurationListEntry[],
    entry: { pillType; configuration },
    removeLanguage?
  ) {
    const languageConfigsEntry = source.find(
      (c) => c.languageId === entry.configuration.language_id
    );
    const languageConfigs = languageConfigsEntry.configurations;

    languageConfigs.splice(languageConfigs.indexOf(entry), 1);

    if (removeLanguage && !languageConfigs.length) {
      source.splice(source.indexOf(languageConfigsEntry), 1);
    }
  }

  private addConfigurationTo(
    target: ConfigurationListEntry[],
    entry: { pillType; configuration },
    source?: ConfigurationListEntry[],
    filteredSource?: ConfigurationListEntry[]
  ) {
    let includedLanguages = target.find((c) => c.languageId === entry.configuration.language_id);
    if (!includedLanguages) {
      includedLanguages = {
        languageId: entry.configuration.language_id,
        languageName: entry.configuration.language_name,
        configurations: [],
      };
      target.push(includedLanguages);

      target.sort((c1, c2) => c1.languageName.localeCompare(c2.languageName));
    }

    includedLanguages.configurations.push(entry);

    if (filteredSource && includedLanguages.configurations.length === 2) {
      const replacedConfig = includedLanguages.configurations.splice(0, 1);
      this.addConfigurationTo(source, replacedConfig[0]);
      this.addConfigurationTo(filteredSource, replacedConfig[0]);
    }

    includedLanguages.configurations.sort((c1, c2) =>
      c1.configuration.name.localeCompare(c2.configuration.name)
    );
  }

  private triggerChangeDetection(): void {
    this.filteredAvailableConfigurations = [...this.filteredAvailableConfigurations];
    this.includedConfigurations = [...this.includedConfigurations];
  }
}
